import axios from "axios";

import { apiGet, apiPost, isHttpError } from "./apiService";

const endpoints = {
  bulk_write: "/data-remote/bulk/write",
  bulk_status: "/data-remote/bulk/status",
};

export const BULK_UPLOAD_CONTENT_TYPE = "application/zip";

export interface BulkFieldMapping {
  api_name: string;
  index: number;
  find_by?: string | null;
  default_value?: string | null;
  format?: string | null;
}

export interface BulkWriteResource {
  type: string;
  module: { api_name: string };
  file_names: string[];
  find_by?: string | null;
  field_mappings: BulkFieldMapping[];
}

export interface BulkWriteRequest {
  operation: string;
  resources: BulkWriteResource[];
  filename: string;
  content_type: string;
  character_encoding?: string | null;
  ignore_empty?: boolean | null;
}

export interface BulkWriteResponse {
  job_id: string;
  upload_url: string;
}

export interface BulkStatusResponse {
  job_id: string;
  status: string | null;
  created_at: number | null;
  updated_at: string | null;
  zoho_job_response: Record<string, unknown> | null;
  callback: Record<string, unknown> | null;
}

export const stageBulkWrite = (payload: BulkWriteRequest): Promise<BulkWriteResponse> =>
  apiPost<BulkWriteResponse>(endpoints.bulk_write, payload);

// The presigned URL is a direct-to-S3 PUT: it must NOT go through the
// authenticated apiFetch/apiPost helpers, since adding an Authorization header
// would invalidate the S3 signature. Content-Type must exactly match the
// content_type sent in the step-1 request body.
export const uploadBulkFile = async (
  uploadUrl: string,
  file: File,
  contentType: string,
): Promise<void> => {
  await axios.put(uploadUrl, file, {
    headers: { "Content-Type": contentType },
  });
};

export const getBulkStatus = (jobId: string): Promise<BulkStatusResponse> =>
  apiGet<BulkStatusResponse>(`${endpoints.bulk_status}/${encodeURIComponent(jobId)}`);

// The backend returns two different error body shapes: { error: string }
// for most failures, and FastAPI's default { detail: [...] } for 422s.
export const extractBulkErrorMessage = (error: unknown): string => {
  if (isHttpError(error)) {
    const data = error.response.data;
    if (data && typeof data === "object") {
      if ("error" in data && typeof data.error === "string") {
        return (data as { error: string }).error;
      }
      if ("detail" in data) {
        const { detail } = data;
        if (Array.isArray(detail)) {
          return detail
            .map((item) =>
              item && typeof item === "object" && "msg" in item
                ? String((item as { msg: unknown }).msg)
                : JSON.stringify(item),
            )
            .join("; ");
        }
        if (typeof detail === "string") return detail;
      }
    }
  }
  return "Something went wrong. Please try again.";
};
